import { Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { FileUpload } from '../../../components/FileUpload';
import { useT } from '../../../i18n/I18nProvider';
import type { ParsedComponent } from '../../../lib/template-components';

/** The header formats WhatsApp accepts, plus "no header at all". */
export const HEADER_FORMATS = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'] as const;
export type HeaderFormat = (typeof HEADER_FORMATS)[number];

export function isMediaFormat(f: string | undefined): boolean {
  return f === 'IMAGE' || f === 'VIDEO' || f === 'DOCUMENT';
}

/**
 * What each header format will take, narrowed to the types WhatsApp itself
 * accepts. Offering a .heic for an image header only moves the rejection from
 * this form to Meta's review queue, hours later.
 */
export const ACCEPT_FOR: Record<string, string[]> = {
  IMAGE: ['image/jpeg', 'image/png'],
  VIDEO: ['video/mp4', 'video/3gpp'],
  DOCUMENT: [
    'application/pdf', 'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain'
  ]
};

/** What the upload field will take for a given header format. */
export function acceptForHeader(format: string | undefined): string[] | undefined {
  return ACCEPT_FOR[(format ?? '').toUpperCase()];
}

/**
 * The header of a WhatsApp template.
 *
 * A template may carry one header, and it is either a line of text or a piece
 * of media: an image, a video or a document. The editor was a raw JSON
 * textarea, so a media header was authorable only by someone who already knew
 * Meta's component schema by heart, and nothing on the page hinted that images,
 * videos and documents were possible at all.
 *
 * The sample URL is not decoration. Meta requires an example for a media header
 * at approval, and a template submitted without one is rejected, hours later,
 * with a message the tenant has to go and look for.
 */
export function HeaderBuilder({
  format,
  text,
  sampleUrl,
  onChange
}: {
  format: HeaderFormat;
  text: string;
  sampleUrl: string;
  onChange: (next: { format: HeaderFormat; text: string; sampleUrl: string }) => void;
}) {
  const t = useT();

  return (
    <Stack gap="sm">
      <Group gap="md" grow align="flex-start">
        <Select
          label={t('templates.header.label')}
          description={t('templates.header.hint')}
          value={format}
          onChange={(e) => onChange({ format: e.currentTarget.value as HeaderFormat, text, sampleUrl })}
          data={HEADER_FORMATS.map((f) => ({ value: f, label: t(`templates.header.format.${f.toLowerCase()}`) }))}
        />

        {format === 'TEXT' && (
          <TextInput
            label={t('templates.header.text')}
            description={t('templates.header.text.hint')}
            value={text}
            onChange={(e) => onChange({ format, text: e.currentTarget.value, sampleUrl })}
          />
        )}

        {isMediaFormat(format) && (
          /*
           * Typing a URL here means having already hosted the file somewhere
           * public, which most tenants cannot do, so the field uploads instead
           * and stores the signed URL it gets back. Meta fetches that URL
           * anonymously at review time, which is exactly what it is for.
           */
          <FileUpload
            label={t('templates.header.sample')}
            description={t('templates.header.sample.hint')}
            accept={acceptForHeader(format)}
            value={sampleUrl}
            onChange={(next) => onChange({ format, text, sampleUrl: next?.url ?? '' })}
          />
        )}
      </Group>

      {isMediaFormat(format) && (
        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('templates.header.sendHint')}
        </Text>
      )}
    </Stack>
  );
}

/**
 * Read the header out of a components array.
 *
 * A media header's example lives at `example.header_handle[0]`, which is where
 * Meta puts it and where an imported template will have it.
 */
export function readHeader(parsed: ParsedComponent[]): { format: HeaderFormat; text: string; sampleUrl: string } {
  const header = parsed.find((c) => c.type === 'HEADER');
  if (!header) return { format: 'NONE', text: '', sampleUrl: '' };
  const format = (header.format ?? 'TEXT').toUpperCase() as HeaderFormat;
  return {
    format: (HEADER_FORMATS as readonly string[]).includes(format) ? format : 'TEXT',
    text: header.text ?? '',
    sampleUrl: header.example?.header_handle?.[0] ?? ''
  };
}

/**
 * Write the header back into a components array, replacing whatever was there.
 *
 * The rest of the array is left exactly as it was: the JSON editor beside this
 * is still the authority on body, footer and buttons, and rewriting the whole
 * document from a form would silently drop anything the form does not model.
 */
export function writeHeader(
  componentsJson: string,
  header: { format: HeaderFormat; text: string; sampleUrl: string }
): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(componentsJson);
  } catch {
    return componentsJson; // Invalid JSON is the editor's problem to report.
  }
  if (!Array.isArray(parsed)) return componentsJson;

  const rest = parsed.filter((c) => String((c as { type?: unknown })?.type ?? '').toUpperCase() !== 'HEADER');
  if (header.format === 'NONE') return JSON.stringify(rest, null, 2);

  const block: Record<string, unknown> = { type: 'HEADER', format: header.format };
  if (header.format === 'TEXT') block.text = header.text;
  else if (header.sampleUrl.trim()) block.example = { header_handle: [header.sampleUrl.trim()] };

  // The header is always first: Meta renders components in order and a header
  // below the body is not a header.
  return JSON.stringify([block, ...rest], null, 2);
}
